import { useState } from 'react'
import { FileText } from 'lucide-react'
import { toast } from 'sonner'
import { toDateInputValue } from '@/components/Deals/DealDetailPanel/legalDataUtils'
import { clientesService, type ContratoAssinaturaPreview } from '@/services/clientesService'
import { formatDateTime } from '@/utils/format-date'
import { getFriendlyError } from '@/utils/getFriendlyError'

const UNSIGNED_PDF_MESSAGE =
  'Este PDF não contém certificado de assinatura (D4Sign). Selecione o contrato assinado com a página de certificado.'

const fieldClass =
  'w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm text-text-primary'

type Resolved = { file: File; signedAt: string }

type Props = {
  disabled?: boolean
  onResolved: (result: Resolved | null) => void
}

export function CadastroContratoAssinadoField({ disabled, onResolved }: Props) {
  const [fileName, setFileName] = useState('')
  const [parsing, setParsing] = useState(false)
  const [assinaturas, setAssinaturas] = useState<ContratoAssinaturaPreview[]>([])
  const [signedAt, setSignedAt] = useState('')
  const [selectedKey, setSelectedKey] = useState('manual')
  const [unsigned, setUnsigned] = useState(false)
  const [file, setFile] = useState<File | null>(null)

  const publish = (nextFile: File | null, nextDate: string, list: ContratoAssinaturaPreview[]) => {
    if (!nextFile || list.length === 0 || !nextDate) {
      onResolved(null)
      return
    }
    onResolved({ file: nextFile, signedAt: nextDate })
  }

  const handleFile = async (next: File | null) => {
    setFile(next)
    setFileName(next?.name ?? '')
    setAssinaturas([])
    setSignedAt('')
    setSelectedKey('manual')
    setUnsigned(false)
    onResolved(null)
    if (!next) return
    if (next.size > 10 * 1024 * 1024 || (!next.type.includes('pdf') && !next.name.toLowerCase().endsWith('.pdf'))) {
      toast.error('Envie um PDF de até 10 MB')
      setFile(null)
      setFileName('')
      return
    }
    setParsing(true)
    try {
      const preview = await clientesService.previewContratoAssinado(next)
      setAssinaturas(preview.assinaturas)
      if (preview.assinaturas.length === 0) {
        setUnsigned(true)
        toast.error(UNSIGNED_PDF_MESSAGE)
        return
      }
      const date = toDateInputValue(preview.ultimoAssinadoEm) || toDateInputValue(new Date())
      const lastIdx = preview.assinaturas.findIndex((item) => item.assinadoEm === preview.ultimoAssinadoEm)
      setSelectedKey(lastIdx >= 0 ? String(lastIdx) : 'manual')
      setSignedAt(date)
      publish(next, date, preview.assinaturas)
    } catch (err) {
      setUnsigned(true)
      toast.error(getFriendlyError(err, 'Não foi possível ler as assinaturas do PDF'))
    } finally {
      setParsing(false)
    }
  }

  const withDates = assinaturas.filter((item) => item.assinadoEm)

  return (
    <div className='grid gap-2 lg:col-span-2'>
      <span className='text-xs text-text-muted'>PDF do contrato assinado</span>
      <label className='flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[var(--glass-border)] px-3 py-2'>
        <FileText size={16} className='shrink-0 text-accent' />
        <span className='min-w-0 flex-1 truncate text-sm text-text-primary'>
          {parsing ? 'Lendo certificado…' : fileName || 'Selecionar PDF (máx. 10 MB)'}
        </span>
        <input
          type='file'
          accept='application/pdf,.pdf'
          className='hidden'
          disabled={disabled || parsing}
          onChange={(event) => void handleFile(event.target.files?.[0] ?? null)}
        />
      </label>
      {unsigned && (
        <p className='rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300' role='alert'>
          {UNSIGNED_PDF_MESSAGE}
        </p>
      )}
      {assinaturas.length > 0 && (
        <ul className='grid gap-2 sm:grid-cols-2'>
          {assinaturas.map((item, index) => (
            <li key={`${item.email ?? item.nome}-${index}`} className='rounded-lg border border-[var(--glass-border)] px-3 py-2 text-sm'>
              <p className='font-medium text-text-primary'>{item.nome}</p>
              {item.email && <p className='truncate text-xs text-text-muted'>{item.email}</p>}
              <p className='text-xs text-text-muted'>
                {item.assinadoEm ? formatDateTime(item.assinadoEm) : 'Data não encontrada no PDF'}
              </p>
            </li>
          ))}
        </ul>
      )}
      {assinaturas.length > 0 && (
        <label className='grid max-w-xs gap-1 text-xs text-text-muted'>
          Data de assinatura
          <input
            type='date'
            className={fieldClass}
            value={signedAt}
            max={toDateInputValue(new Date())}
            disabled={disabled}
            onChange={(event) => {
              setSelectedKey('manual')
              setSignedAt(event.target.value)
              publish(file, event.target.value, assinaturas)
            }}
          />
          {selectedKey !== 'manual' && withDates.length > 0 && (
            <span>Padrão: última assinatura do certificado.</span>
          )}
        </label>
      )}
    </div>
  )
}
